/**
 * Reason a service case write was rejected by the backend.
 *
 * The backend answers with a BmwResponse whose "errors" array carries an application specific code in addition to the
 * HTTP status. Both are kept here so that callers can tell a rejected write of a locked service case
 * (409 / AWP_SERVICE_CASE_LOCKED) apart from any other failure instead of parsing the error message.
 */
export class ServiceCaseUpdateError extends Error {
  private static readonly LOCKED_STATUS_CODE: number = 409;

  private static readonly LOCKED_ERROR_CODE: string = 'AWP_SERVICE_CASE_LOCKED';

  constructor(readonly statusCode: number, readonly errorCode: string, message: string) {
    super(message);
  }

  /**
   * Creates the error from a failed HTTP response, using the first error reported in the response body.
   * @param response The error response of the failed request.
   */
  static fromHttpErrorResponse(response: any): ServiceCaseUpdateError {
    const firstError: any = response?.error?.errors?.at(0);
    return new ServiceCaseUpdateError(response?.status, firstError?.code, firstError?.message);
  }

  /**
   * True, if the given rejection reason states that the service case is locked against modification.
   * @param reason The reason a service case update promise was rejected with.
   */
  static isServiceCaseLocked(reason: unknown): boolean {
    return reason instanceof ServiceCaseUpdateError
      && (reason.statusCode === ServiceCaseUpdateError.LOCKED_STATUS_CODE
        || reason.errorCode === ServiceCaseUpdateError.LOCKED_ERROR_CODE);
  }

  /**
   * Returns the message of the given rejection reason, which is not necessarily a ServiceCaseUpdateError.
   * @param reason The reason a service case update promise was rejected with.
   */
  static getMessage(reason: unknown): string {
    return reason instanceof ServiceCaseUpdateError ? reason.message : reason as string;
  }
}
